import { CHECKOUT_COUNTRIES } from '@/lib/checkout/types'

import type { SiteLocale } from '@/graphql'

import type { SeoMetadata } from './datocms-schema'

export const LOCALE_GB = 'en-gb'
export const LOCALE_US = 'en-us'
export const LOCALE_IE = 'en-ie'
export const LOCALE_IT = 'it'
export const LOCALE_DE = 'de'
export const LOCALE_ES = 'es'
export const LOCALE_NL = 'nl'
export const LOCALE_FR = 'fr'
export const LOCALE_AU = 'en-au'
export const LOCALE_NZ = 'en-nz'
export const LOCALE_AT = 'de-at'

// Every locale the site can serve. A new market is added here and nowhere else in
// this block — deliberately not composed out of EU_LOCALES, so adding a non-European
// market can't make it European by construction.
const ALL_SUPPORTED_LOCALES = [
  LOCALE_GB,
  LOCALE_US,
  LOCALE_IE,
  LOCALE_IT,
  LOCALE_DE,
  LOCALE_ES,
  LOCALE_NL,
  LOCALE_FR,
  LOCALE_AU,
  LOCALE_NZ,
  LOCALE_AT,
] as const

export type WebsiteLocale = (typeof ALL_SUPPORTED_LOCALES)[number]

// GB + US only — for pages that serve no rollout locales
export const BASE_WEBSITE_LOCALES: readonly WebsiteLocale[] = [LOCALE_GB, LOCALE_US]

// Locales that are actually in the EU — for content and legal pages that are
// EU-specific. Not the same as "any locale that isn't GB/US": that set is about
// page coverage and will include non-EU markets such as Australia.
export const EU_LOCALES: readonly WebsiteLocale[] = [
  LOCALE_IE,
  LOCALE_IT,
  LOCALE_DE,
  LOCALE_ES,
  LOCALE_NL,
  LOCALE_FR,
  LOCALE_AT,
]

// All possible locales — for global utilities that need to recognise any locale in URLs/paths
export const ALL_WEBSITE_LOCALES: readonly WebsiteLocale[] = ALL_SUPPORTED_LOCALES

const ALL_SUPPORTED_LOCALES_SET = new Set(ALL_SUPPORTED_LOCALES)
export const isWebsiteLocale = (locale: string): locale is WebsiteLocale =>
  ALL_SUPPORTED_LOCALES_SET.has(locale as WebsiteLocale)

export const HOMEPAGE_PATH = '/'
export const DAILY30_PDP_PATH = '/daily30'
export const MEMBERSHIP_PDP_PATH = '/buymembership'
export const APP_DOWNLOAD_PATH = '/app'
export const BARS_PDP_PATH = '/buyguthealthbar'
export const FAQS_PATH = '/faqs'

export type PageLocaleEntry = {
  /** URL path to the page, no locale prefix (e.g. '/', '/daily30'). */
  path: string
  /** Next.js route pattern (`router.pathname`) for client-side checks. */
  routePattern: string
  /** When true, subpaths inherit the same locale support (e.g. /daily30/plans). */
  allowSubpaths: boolean
  /** Locales the page always serves. An env var adds to these; it can never remove them. */
  locales: readonly WebsiteLocale[]
  /**
   * Raw value of this page's NEXT_PUBLIC_*_LOCALES variable, or undefined when the
   * page isn't env-configurable. Must be a literal `process.env.X` read: Next only
   * inlines literal references into the client bundle, so a computed lookup would
   * resolve to undefined in the browser.
   */
  envOverride: string | undefined
}

// Locale rollout is driven per page from Vercel environment variables, so a locale
// can go to Preview, be checked, then Production without a code change. Rollout
// locales live only in the variable, so dropping one from it turns that locale off;
// `locales` is the set no value can take away, which means a typo narrows a page
// rather than emptying it.
export const resolvePageLocales = (entry: PageLocaleEntry): readonly WebsiteLocale[] =>
  Array.from(
    new Set<WebsiteLocale>([
      ...entry.locales,
      ...(entry.envOverride ?? '')
        .split(',')
        .map((value) => value.trim())
        .filter(isWebsiteLocale),
    ]),
  )

const HOMEPAGE_ENTRY: PageLocaleEntry = {
  path: HOMEPAGE_PATH,
  routePattern: '/[locale]',
  allowSubpaths: false,
  locales: [LOCALE_GB, LOCALE_US],
  envOverride: process.env.NEXT_PUBLIC_HOMEPAGE_LOCALES,
}

const DAILY30_PDP_ENTRY: PageLocaleEntry = {
  path: DAILY30_PDP_PATH,
  routePattern: '/[locale]/daily30',
  allowSubpaths: true,
  locales: [LOCALE_GB, LOCALE_US],
  envOverride: process.env.NEXT_PUBLIC_DAILY30_LOCALES,
}

const MEMBERSHIP_PDP_ENTRY: PageLocaleEntry = {
  path: MEMBERSHIP_PDP_PATH,
  routePattern: '/[locale]/buymembership',
  allowSubpaths: false,
  locales: [LOCALE_GB, LOCALE_US],
  envOverride: process.env.NEXT_PUBLIC_MEMBERSHIP_LOCALES,
}

const APP_DOWNLOAD_ENTRY: PageLocaleEntry = {
  path: APP_DOWNLOAD_PATH,
  routePattern: '/[locale]/app',
  allowSubpaths: false,
  locales: [LOCALE_GB, LOCALE_US],
  envOverride: process.env.NEXT_PUBLIC_MEMBERSHIP_LOCALES,
}

const BARS_PDP_ENTRY: PageLocaleEntry = {
  path: BARS_PDP_PATH,
  routePattern: '/[locale]/buyguthealthbar',
  allowSubpaths: false,
  locales: [LOCALE_GB],
  envOverride: process.env.NEXT_PUBLIC_BARS_LOCALES,
}

// One DatoCMS FAQ Page record per country, so every locale here needs a published record
// or the build fails.
const FAQS_ENTRY: PageLocaleEntry = {
  path: FAQS_PATH,
  routePattern: '/[locale]/faqs',
  allowSubpaths: false,
  locales: [LOCALE_GB, LOCALE_US],
  envOverride: process.env.NEXT_PUBLIC_FAQS_LOCALES,
}

// Single source of truth for which locales each page serves. Middleware, the locale
// picker and the mismatch banner all derive their behaviour from this list.
export const PAGE_LOCALE_REGISTRY: readonly PageLocaleEntry[] = [
  HOMEPAGE_ENTRY,
  DAILY30_PDP_ENTRY,
  MEMBERSHIP_PDP_ENTRY,
  APP_DOWNLOAD_ENTRY,
  BARS_PDP_ENTRY,
  FAQS_ENTRY,
]

export const HOMEPAGE_LOCALES: readonly WebsiteLocale[] = resolvePageLocales(HOMEPAGE_ENTRY)

export const DAILY30_LOCALES: readonly WebsiteLocale[] = resolvePageLocales(DAILY30_PDP_ENTRY)

export const MEMBERSHIP_LOCALES: readonly WebsiteLocale[] = resolvePageLocales(MEMBERSHIP_PDP_ENTRY)

export const BAR_LOCALES: readonly WebsiteLocale[] = resolvePageLocales(BARS_PDP_ENTRY)

export const FAQS_LOCALES: readonly WebsiteLocale[] = resolvePageLocales(FAQS_ENTRY)

export const WEBSITE_DEFAULT_LOCALE = LOCALE_GB

export const ALL_COUNTRY_CODES = ['GB', 'US', 'IE', 'IT', 'DE', 'ES', 'NL', 'FR', 'AU', 'NZ', 'AT'] as const
export type CountryCode = (typeof ALL_COUNTRY_CODES)[number]

export const LOCALISED_PAGES = [
  '/',
  '/buymembership',
  '/buymembership/plans',
  '/buyguthealthtest',
  '/buyguthealthbar',
  '/daily30',
  '/daily30-pmax',
  '/daily30/plans',
  '/daily30/privacy-policy',
  '/faqs',
  '/terms',
  '/buyapptest',
  '/app',
  '/referral-terms',
  '/referral-terms/exclusive-offer',
  '/host-competition/terms',
] as const

/** Map of DatoCMS locales to boolean indicating whether the app should fetch content for that locale. */
const enabledDatocmsLocales: Record<SiteLocale, boolean> = {
  en: true, // English (UK)
  en_US: true, // English (US)

  /* Rollout locales */
  en_IE: true, // English (Ireland)
  it: true, // Italian
  de: true, // German
  es: true, // Spanish
  nl: true, // Dutch
  fr: true, // French
  en_AU: true, // English (Australia)
  en_NZ: true, // English (New Zealand)
  de_AT: true, // German (Austria)
}

export type DatoCMSLocale = SiteLocale

export const DATOCMS_LOCALES = ((): readonly DatoCMSLocale[] => {
  // Fetched locales = every enabled Dato locale if any per-PDP list includes a non-base locale,
  // otherwise base only.
  const anyRolloutLocaleConfigured = [
    ...HOMEPAGE_LOCALES,
    ...DAILY30_LOCALES,
    ...MEMBERSHIP_LOCALES,
    ...BAR_LOCALES,
  ].some((locale) => !BASE_WEBSITE_LOCALES.includes(locale))

  if (anyRolloutLocaleConfigured) {
    return Object.entries(enabledDatocmsLocales).reduce<SiteLocale[]>((acc, [locale, enabled]) => {
      if (enabled) acc.push(locale as SiteLocale)
      return acc
    }, [])
  } else {
    const locales: DatoCMSLocale[] = ['en', 'en_US']
    return locales.filter((locale) => enabledDatocmsLocales[locale])
  }
})()

export interface LocaleSupportedPageRecord {
  id: string
  metadata: SeoMetadata
}

export type LocaleSupportedPageRecordCollection = Record<DatoCMSLocale, LocaleSupportedPageRecord>

export type LocalisedPageProps = {
  localisedPages: LocaleSupportedPageRecordCollection
}

export type CountryDisplayInfo = {
  code: CountryCode
  name: string
  flag: string
  locale: WebsiteLocale
}

export const COUNTRY_DISPLAY_BY_CODE: Record<CountryCode, CountryDisplayInfo> = {
  GB: { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', locale: LOCALE_GB },
  US: { code: 'US', name: 'United States', flag: '🇺🇸', locale: LOCALE_US },
  IE: { code: 'IE', name: 'Ireland', flag: '🇮🇪', locale: LOCALE_IE },
  IT: { code: 'IT', name: 'Italy', flag: '🇮🇹', locale: LOCALE_IT },
  DE: { code: 'DE', name: 'Germany', flag: '🇩🇪', locale: LOCALE_DE },
  ES: { code: 'ES', name: 'Spain', flag: '🇪🇸', locale: LOCALE_ES },
  NL: { code: 'NL', name: 'Netherlands', flag: '🇳🇱', locale: LOCALE_NL },
  FR: { code: 'FR', name: 'France', flag: '🇫🇷', locale: LOCALE_FR },
  AU: { code: 'AU', name: 'Australia', flag: '🇦🇺', locale: LOCALE_AU },
  NZ: { code: 'NZ', name: 'New Zealand', flag: '🇳🇿', locale: LOCALE_NZ },
  AT: { code: 'AT', name: 'Austria', flag: '🇦🇹', locale: LOCALE_AT },
}

export const LOCALE_TO_COUNTRY_DISPLAY_MAP: Record<WebsiteLocale, CountryDisplayInfo> = {
  [LOCALE_GB]: COUNTRY_DISPLAY_BY_CODE.GB,
  [LOCALE_US]: COUNTRY_DISPLAY_BY_CODE.US,
  [LOCALE_IE]: COUNTRY_DISPLAY_BY_CODE.IE,
  [LOCALE_IT]: COUNTRY_DISPLAY_BY_CODE.IT,
  [LOCALE_DE]: COUNTRY_DISPLAY_BY_CODE.DE,
  [LOCALE_ES]: COUNTRY_DISPLAY_BY_CODE.ES,
  [LOCALE_NL]: COUNTRY_DISPLAY_BY_CODE.NL,
  [LOCALE_FR]: COUNTRY_DISPLAY_BY_CODE.FR,
  [LOCALE_AU]: COUNTRY_DISPLAY_BY_CODE.AU,
  [LOCALE_NZ]: COUNTRY_DISPLAY_BY_CODE.NZ,
  [LOCALE_AT]: COUNTRY_DISPLAY_BY_CODE.AT,
}

export const localeToCountryCode = (locale: string): CountryCode =>
  locale in LOCALE_TO_COUNTRY_DISPLAY_MAP ? LOCALE_TO_COUNTRY_DISPLAY_MAP[locale as WebsiteLocale].code : 'US'

export const countryCodeToLocale = (country: string): string =>
  country in COUNTRY_DISPLAY_BY_CODE
    ? COUNTRY_DISPLAY_BY_CODE[country as keyof typeof COUNTRY_DISPLAY_BY_CODE].locale
    : WEBSITE_DEFAULT_LOCALE

// Countries where ZOE is the seller of record for food products, and so needs its own
// Terms of Service. Not CHECKOUT_COUNTRIES (the Stripe storefront) and not page coverage.
export const FOOD_DIRECT_SALE_COUNTRIES: readonly CountryCode[] = ['GB', 'US', 'AU', 'NZ']

export const isFoodDirectSaleLocale = (locale: WebsiteLocale): boolean =>
  FOOD_DIRECT_SALE_COUNTRIES.includes(localeToCountryCode(locale))

const hasActiveFoodPdp = (locale: WebsiteLocale): boolean =>
  DAILY30_LOCALES.includes(locale) || BAR_LOCALES.includes(locale)

// Terms follow the product: a market needs them once we sell there and the PDP is
// switched on, so stage can carry them ahead of a launch without production doing so.
export const FOOD_TERMS_LOCALES: readonly WebsiteLocale[] = ALL_WEBSITE_LOCALES.filter(
  (locale) => isFoodDirectSaleLocale(locale) && hasActiveFoodPdp(locale),
)

const takesPaymentIn = (locale: WebsiteLocale): boolean =>
  (CHECKOUT_COUNTRIES as readonly CountryCode[]).includes(localeToCountryCode(locale))

export const BAR_CHECKOUT_LOCALES: readonly WebsiteLocale[] = BAR_LOCALES.filter(takesPaymentIn)

export const DAILY30_CHECKOUT_LOCALES: readonly WebsiteLocale[] = DAILY30_LOCALES.filter(takesPaymentIn)
